import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { DownloadModal } from '../components/assets/DownloadModal';
import { useToast } from '../hooks/use-toast';
import {
  reviewService,
  ReviewQueueItem,
  ReviewCounts,
  ReviewStatus,
  ReviewAction,
  ReviewHistoryEntry,
  DownloadStats,
} from '../services/review';
import {
  Search,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Download,
  History,
  MessageSquare,
  RotateCcw,
  Sparkles,
  Eraser,
  Maximize,
  Replace,
  Palette,
  Shirt,
  Home,
  ImageIcon,
  Wand2,
  ChevronUp,
  ChevronDown,
  Keyboard,
  GripVertical,
  ZoomIn,
  ZoomOut,
  SplitSquareHorizontal,
  Columns,
  Square,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { format } from 'date-fns';

// Status colors and labels
const STATUS_CONFIG: Record<ReviewStatus, { label: string; color: string; bgColor: string; borderColor: string; icon: React.ReactNode }> = {
  none: { label: 'Not Submitted', color: 'text-gray-400', bgColor: 'bg-gray-500/10', borderColor: 'border-gray-500/30', icon: null },
  pending: { label: 'Pending', color: 'text-amber-500', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/30', icon: <Clock className="w-4 h-4" /> },
  in_review: { label: 'In Review', color: 'text-blue-500', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/30', icon: <Eye className="w-4 h-4" /> },
  approved: { label: 'Approved', color: 'text-emerald-500', bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/30', icon: <CheckCircle className="w-4 h-4" /> },
  rejected: { label: 'Rejected', color: 'text-red-500', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/30', icon: <XCircle className="w-4 h-4" /> },
  revision_requested: { label: 'Revision', color: 'text-orange-500', bgColor: 'bg-orange-500/10', borderColor: 'border-orange-500/30', icon: <RotateCcw className="w-4 h-4" /> },
};

// Transformation icons
const TRANSFORMATION_CONFIG: Record<string, { icon: React.ReactNode; label: string }> = {
  background_removal: { icon: <Eraser className="w-3 h-3" />, label: 'BG Remove' },
  upscale: { icon: <Maximize className="w-3 h-3" />, label: 'Upscale' },
  generative_fill: { icon: <Sparkles className="w-3 h-3" />, label: 'Gen Fill' },
  generative_remove: { icon: <Eraser className="w-3 h-3" />, label: 'Gen Remove' },
  generative_replace: { icon: <Replace className="w-3 h-3" />, label: 'Gen Replace' },
  generative_recolor: { icon: <Palette className="w-3 h-3" />, label: 'Recolor' },
  invisible_mannequin: { icon: <Shirt className="w-3 h-3" />, label: 'Ghost' },
  virtual_staging: { icon: <Home className="w-3 h-3" />, label: 'Staging' },
  format_conversion: { icon: <ImageIcon className="w-3 h-3" />, label: 'Convert' },
  gemini_studio_retouch: { icon: <Sparkles className="w-3 h-3" />, label: 'Retouch' },
  studio_retouch: { icon: <Sparkles className="w-3 h-3" />, label: 'Retouch' },
  resize: { icon: <Maximize className="w-3 h-3" />, label: 'Resize' },
  quality: { icon: <Sparkles className="w-3 h-3" />, label: 'Quality' },
};

const getTransformationInfo = (type: string) => {
  const normalizedType = type.toLowerCase().replace(/\s+/g, '_');
  if (normalizedType.startsWith('quality')) {
    return TRANSFORMATION_CONFIG.quality;
  }
  return TRANSFORMATION_CONFIG[type] || TRANSFORMATION_CONFIG[normalizedType] || {
    icon: <Wand2 className="w-3 h-3" />,
    label: type.slice(0, 10)
  };
};

// Image metadata type
interface ImageMetadata {
  width: number;
  height: number;
  fileSize?: number;
}

// View modes
type ViewMode = 'slider' | 'split' | 'single';
type SingleViewImage = 'original' | 'processed';

export function ReviewDashboard() {
  const { toast } = useToast();
  const containerRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [counts, setCounts] = useState<ReviewCounts | null>(null);
  const [queueItems, setQueueItems] = useState<ReviewQueueItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [statusFilter, setStatusFilter] = useState<ReviewStatus | 'all'>('pending');
  const [searchQuery, setSearchQuery] = useState('');

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('single');
  const [singleViewImage, setSingleViewImage] = useState<SingleViewImage>('processed');
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Compare state
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [zoom, setZoom] = useState(1);

  // Review action state
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<ReviewAction | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');

  // History modal state
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<ReviewHistoryEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Keyboard shortcuts help
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);

  // Download state
  const [downloadStats, setDownloadStats] = useState<DownloadStats | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  // Image metadata state
  const [originalMeta, setOriginalMeta] = useState<ImageMetadata | null>(null);
  const [processedMeta, setProcessedMeta] = useState<ImageMetadata | null>(null);

  // Load data
  const loadData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true);

      const statusesToFetch: ReviewStatus[] = statusFilter === 'all'
        ? ['pending', 'in_review', 'approved', 'rejected']
        : [statusFilter];

      const [countsData, downloadStatsData, ...queueResults] = await Promise.all([
        reviewService.getCounts(),
        reviewService.getDownloadStats(),
        ...statusesToFetch.map(status => reviewService.getQueue({ status, limit: 100 }))
      ]);

      setCounts(countsData);
      setDownloadStats(downloadStatsData);

      const allItems = queueResults.flatMap(result => result.items);
      setQueueItems(allItems);

      // Reset selection if out of bounds
      if (selectedIndex >= allItems.length) {
        setSelectedIndex(Math.max(0, allItems.length - 1));
      }
    } catch (err: any) {
      console.error('Failed to load review data:', err);
      toast({
        title: 'Error',
        description: 'Failed to load review queue',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [toast, statusFilter, selectedIndex]);

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData(false);
  };

  // Handle download approved images
  const handleDownloadApproved = async () => {
    setIsDownloading(true);
    try {
      const result = await reviewService.bulkDownloadApproved();
      if (result.downloaded > 0) {
        toast({
          title: 'Download Complete',
          description: `Successfully downloaded ZIP with ${result.downloaded} approved image${result.downloaded !== 1 ? 's' : ''}.`,
        });
        const newStats = await reviewService.getDownloadStats();
        setDownloadStats(newStats);
      }
    } catch (error: any) {
      toast({
        title: 'Download Error',
        description: error.message || 'An error occurred during download.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle download all approved images
  const handleDownloadAllApproved = async () => {
    setIsDownloading(true);
    try {
      const result = await reviewService.bulkDownloadAllApproved();
      if (result.downloaded > 0) {
        toast({
          title: 'Download Complete',
          description: `Successfully downloaded ZIP with ${result.downloaded} approved image${result.downloaded !== 1 ? 's' : ''}.`,
        });
        const newStats = await reviewService.getDownloadStats();
        setDownloadStats(newStats);
      }
    } catch (error: any) {
      toast({
        title: 'Download Error',
        description: error.message || 'An error occurred during download.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle download all processed images
  const handleDownloadAll = async () => {
    setIsDownloading(true);
    try {
      const result = await reviewService.bulkDownloadAll();
      if (result.downloaded > 0) {
        toast({
          title: 'Download Complete',
          description: `Successfully downloaded ZIP with ${result.downloaded} image${result.downloaded !== 1 ? 's' : ''}.`,
        });
        const newStats = await reviewService.getDownloadStats();
        setDownloadStats(newStats);
      }
    } catch (error: any) {
      toast({
        title: 'Download Error',
        description: error.message || 'An error occurred during download.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsDownloading(false);
    }
  };

  // Filter items
  const filteredItems = queueItems.filter((item) => {
    if (!searchQuery) return true;
    return item.original_filename.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const currentItem = filteredItems[selectedIndex];

  // Navigation
  const goToNext = useCallback(() => {
    if (selectedIndex < filteredItems.length - 1) {
      setSelectedIndex(prev => prev + 1);
      setSliderPosition(50);
      setZoom(1);
      setOriginalMeta(null);
      setProcessedMeta(null);
    }
  }, [selectedIndex, filteredItems.length]);

  const goToPrevious = useCallback(() => {
    if (selectedIndex > 0) {
      setSelectedIndex(prev => prev - 1);
      setSliderPosition(50);
      setZoom(1);
      setOriginalMeta(null);
      setProcessedMeta(null);
    }
  }, [selectedIndex]);

  // Load image metadata
  const handleOriginalImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setOriginalMeta({
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
  }, []);

  const handleProcessedImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setProcessedMeta({
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if typing in input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case 'ArrowUp':
        case 'k':
          e.preventDefault();
          goToPrevious();
          break;
        case 'ArrowDown':
        case 'j':
          e.preventDefault();
          goToNext();
          break;
        case 'a':
          if (!e.metaKey && !e.ctrlKey && currentItem) {
            e.preventDefault();
            handleQuickAction('approve');
          }
          break;
        case 'r':
          if (!e.metaKey && !e.ctrlKey && currentItem) {
            e.preventDefault();
            handleQuickAction('reject');
          }
          break;
        case 'v':
          if (!e.metaKey && !e.ctrlKey && currentItem) {
            e.preventDefault();
            setPendingAction('revision');
            setShowNotesDialog(true);
          }
          break;
        case '?':
          e.preventDefault();
          setShowKeyboardHelp(true);
          break;
        case 'Escape':
          if (isFullscreen) {
            setIsFullscreen(false);
          } else {
            setShowKeyboardHelp(false);
            setShowNotesDialog(false);
            setShowHistory(false);
          }
          break;
        case '1':
          e.preventDefault();
          setViewMode('slider');
          break;
        case '2':
          e.preventDefault();
          setViewMode('split');
          break;
        case '3':
          e.preventDefault();
          setViewMode('single');
          break;
        case 'f':
          if (!e.metaKey && !e.ctrlKey) {
            e.preventDefault();
            setIsFullscreen(prev => !prev);
          }
          break;
        case 'o':
          if (!e.metaKey && !e.ctrlKey && viewMode === 'single') {
            e.preventDefault();
            setSingleViewImage('original');
          }
          break;
        case 'p':
          if (!e.metaKey && !e.ctrlKey && viewMode === 'single') {
            e.preventDefault();
            setSingleViewImage('processed');
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrevious, currentItem, isFullscreen, viewMode]);

  // Quick review action (no notes)
  const handleQuickAction = async (action: ReviewAction) => {
    if (!currentItem) return;

    if (action === 'revision') {
      setPendingAction('revision');
      setShowNotesDialog(true);
      return;
    }

    setIsSubmittingReview(true);
    try {
      if (action === 'approve') {
        await reviewService.approve(currentItem.id);
        toast({ title: 'Approved', description: currentItem.original_filename });
      } else if (action === 'reject') {
        await reviewService.reject(currentItem.id);
        toast({ title: 'Rejected', description: currentItem.original_filename });
      }

      // Move to next item and refresh
      goToNext();
      loadData(false);
    } catch (err: any) {
      toast({
        title: 'Action Failed',
        description: err.message || 'Failed to perform review action',
        variant: 'destructive',
      });
    } finally {
      setIsSubmittingReview(false);
    }
  };

  // Review action with notes
  const handleReviewWithNotes = async () => {
    if (!currentItem || !pendingAction) return;

    if (pendingAction === 'revision' && !reviewNotes.trim()) {
      toast({
        title: 'Notes Required',
        description: 'Please provide notes when requesting revision',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmittingReview(true);
    try {
      switch (pendingAction) {
        case 'approve':
          await reviewService.approve(currentItem.id, reviewNotes || undefined);
          toast({ title: 'Approved', description: currentItem.original_filename });
          break;
        case 'reject':
          await reviewService.reject(currentItem.id, reviewNotes || undefined);
          toast({ title: 'Rejected', description: currentItem.original_filename });
          break;
        case 'revision':
          await reviewService.requestRevision(currentItem.id, reviewNotes);
          toast({ title: 'Revision Requested', description: currentItem.original_filename });
          break;
      }

      setShowNotesDialog(false);
      setReviewNotes('');
      setPendingAction(null);
      goToNext();
      loadData(false);
    } catch (err: any) {
      toast({
        title: 'Action Failed',
        description: err.message || 'Failed to perform review action',
        variant: 'destructive',
      });
    } finally {
      setIsSubmittingReview(false);
    }
  };

  // Load history
  const loadHistory = async () => {
    if (!currentItem) return;

    setShowHistory(true);
    setIsLoadingHistory(true);
    try {
      const historyData = await reviewService.getHistory(currentItem.id);
      setHistory(historyData);
    } catch (err: any) {
      toast({
        title: 'Error',
        description: 'Failed to load review history',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Slider handling
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  // Download
  const handleDownload = (url: string, type: 'original' | 'processed') => {
    if (!currentItem) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = `${type}_${currentItem.original_filename}`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const canReview = currentItem && ['pending', 'in_review'].includes(currentItem.review_status);

  return (
    <TooltipProvider>
      <div
        ref={containerRef}
        className={`flex flex-col ${isFullscreen ? 'fixed inset-0 z-50 bg-black' : 'h-[calc(100vh-4rem)]'}`}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Header - Hidden in fullscreen */}
        {!isFullscreen && (
        <div className="flex-shrink-0 px-4 py-2 border-b bg-background">
          <div className="flex items-center justify-between gap-2">
            {/* Left side - Title & Filter */}
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="text-lg font-bold shrink-0">Review</h1>

              {/* Status Filter - Compact */}
              <Select value={statusFilter} onValueChange={(v) => {
                setStatusFilter(v as ReviewStatus | 'all');
                setSelectedIndex(0);
              }}>
                <SelectTrigger className="w-[130px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending">
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-3 h-3 text-amber-500" />
                        Pending ({counts?.pending || 0})
                      </span>
                    </SelectItem>
                    <SelectItem value="in_review">
                      <span className="flex items-center gap-1.5">
                        <Eye className="w-3 h-3 text-blue-500" />
                        Review ({counts?.in_review || 0})
                      </span>
                    </SelectItem>
                    <SelectItem value="approved">
                      <span className="flex items-center gap-1.5">
                        <CheckCircle className="w-3 h-3 text-emerald-500" />
                        Approved ({counts?.approved || 0})
                      </span>
                    </SelectItem>
                    <SelectItem value="rejected">
                      <span className="flex items-center gap-1.5">
                        <XCircle className="w-3 h-3 text-red-500" />
                        Rejected ({counts?.rejected || 0})
                      </span>
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>

              {/* Search - Hidden on very small screens */}
              <div className="relative hidden sm:block">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSelectedIndex(0);
                  }}
                  className="pl-8 h-8 w-[140px] text-xs"
                />
              </div>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-1.5 shrink-0">
              {/* Counter */}
              <span className="text-xs text-muted-foreground tabular-nums px-2">
                {filteredItems.length > 0 ? `${selectedIndex + 1}/${filteredItems.length}` : '0'}
              </span>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 hidden md:inline-flex" onClick={() => setShowKeyboardHelp(true)}>
                    <Keyboard className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Shortcuts (?)</TooltipContent>
              </Tooltip>

              {/* Download Button - Compact */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={(downloadStats?.pendingDownload) ? 'default' : 'outline'}
                    size="icon"
                    className={`h-8 w-8 ${downloadStats?.pendingDownload ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                    onClick={() => setDownloadModalOpen(true)}
                    disabled={isDownloading || !downloadStats?.totalProcessed}
                  >
                    {isDownloading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Download {downloadStats?.pendingDownload ? `(${downloadStats.pendingDownload} new)` : ''}
                </TooltipContent>
              </Tooltip>

              <Button variant="outline" size="icon" className="h-8 w-8" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Left Sidebar - Queue - Hidden in fullscreen, narrower */}
          {!isFullscreen && (
          <div className="w-52 flex-shrink-0 border-r bg-muted/30 flex flex-col hidden md:flex">
            <div className="p-2 border-b">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Queue ({filteredItems.length})
              </span>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                  <ImageIcon className="w-12 h-12 mb-2 opacity-30" />
                  <span className="text-sm">No items</span>
                </div>
              ) : (
                filteredItems.map((item, index) => {
                  const statusConfig = STATUS_CONFIG[item.review_status];
                  const isSelected = index === selectedIndex;

                  return (
                    <button
                      key={item.id}
                      className={`w-full flex items-center gap-3 p-2 text-left transition-colors border-l-2 ${
                        isSelected
                          ? 'bg-primary/10 border-l-primary'
                          : 'border-l-transparent hover:bg-muted/50'
                      }`}
                      onClick={() => {
                        setSelectedIndex(index);
                        setSliderPosition(50);
                        setZoom(1);
                      }}
                    >
                      {/* Thumbnail */}
                      <div className="w-12 h-12 flex-shrink-0 rounded overflow-hidden bg-muted">
                        <img
                          src={item.processed_url || item.original_url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.original_filename}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className={statusConfig.color}>{statusConfig.icon}</span>
                          <span className={`text-xs ${statusConfig.color}`}>{statusConfig.label}</span>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="p-2 border-t flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={goToPrevious}
                disabled={selectedIndex === 0}
              >
                <ChevronUp className="w-4 h-4 mr-1" />
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={goToNext}
                disabled={selectedIndex >= filteredItems.length - 1}
              >
                Next
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
          )}

          {/* Center - Image Compare */}
          <div className="flex-1 flex flex-col bg-black/95 min-h-0 min-w-0 overflow-hidden">
            {currentItem ? (
              <>
                {/* Image Info Bar - Compact */}
                <div className="flex-shrink-0 flex items-center justify-between px-3 py-1.5 bg-black/50 border-b border-white/10 w-full overflow-hidden gap-2">
                  {/* Left: Filename & Transformations */}
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <h2 className="text-white text-sm font-medium truncate">
                      {currentItem.original_filename}
                    </h2>
                    {currentItem.applied_transformations && currentItem.applied_transformations.length > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-1 shrink-0">
                            {currentItem.applied_transformations.slice(0, 2).map((t, i) => {
                              const info = getTransformationInfo(t.type);
                              return (
                                <span key={i} className="text-white/60">
                                  {info.icon}
                                </span>
                              );
                            })}
                            {currentItem.applied_transformations.length > 2 && (
                              <span className="text-white/40 text-xs">+{currentItem.applied_transformations.length - 2}</span>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          {currentItem.applied_transformations.map(t => getTransformationInfo(t.type).label).join(', ')}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>

                  {/* Right: Controls */}
                  <div className="flex items-center gap-1 shrink-0">
                    {/* View Mode Toggle */}
                    <div className="flex items-center bg-white/10 rounded p-0.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-6 w-6 p-0 ${viewMode === 'slider' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white'}`}
                        onClick={() => setViewMode('slider')}
                      >
                        <SplitSquareHorizontal className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-6 w-6 p-0 ${viewMode === 'split' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white'}`}
                        onClick={() => setViewMode('split')}
                      >
                        <Columns className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-6 w-6 p-0 ${viewMode === 'single' ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white'}`}
                        onClick={() => setViewMode('single')}
                      >
                        <Square className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Single View Toggle - Compact */}
                    {viewMode === 'single' && (
                      <div className="flex items-center bg-white/10 rounded p-0.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-6 px-1.5 text-[10px] ${singleViewImage === 'original' ? 'bg-white/20 text-white' : 'text-white/50'}`}
                          onClick={() => setSingleViewImage('original')}
                        >
                          Orig
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-6 px-1.5 text-[10px] ${singleViewImage === 'processed' ? 'bg-emerald-500/30 text-emerald-400' : 'text-white/50'}`}
                          onClick={() => setSingleViewImage('processed')}
                          disabled={!currentItem.processed_url}
                        >
                          Proc
                        </Button>
                      </div>
                    )}

                    {/* Zoom - Compact */}
                    <div className="flex items-center bg-white/10 rounded">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-white/50 hover:text-white"
                        onClick={() => setZoom(prev => Math.max(0.5, prev - 0.25))}
                      >
                        <ZoomOut className="h-3 w-3" />
                      </Button>
                      <button
                        className="text-white/60 text-[10px] w-8 text-center hover:text-white tabular-nums"
                        onClick={() => setZoom(1)}
                      >
                        {Math.round(zoom * 100)}%
                      </button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-white/50 hover:text-white"
                        onClick={() => setZoom(prev => Math.min(3, prev + 0.25))}
                      >
                        <ZoomIn className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Action icons */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-white/50 hover:text-white"
                      onClick={() => setIsFullscreen(prev => !prev)}
                    >
                      {isFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-white/50 hover:text-white hidden sm:inline-flex"
                      onClick={loadHistory}
                    >
                      <History className="h-3.5 w-3.5" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-white/50 hover:text-white"
                      onClick={() => currentItem.processed_url && handleDownload(currentItem.processed_url, 'processed')}
                      disabled={!currentItem.processed_url}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Compare Area */}
                <div
                  ref={sliderRef}
                  className={`flex-1 relative overflow-hidden select-none min-h-0 ${viewMode === 'slider' ? 'cursor-ew-resize' : ''}`}
                  onTouchMove={viewMode === 'slider' ? handleTouchMove : undefined}
                >
                  {/* Slider View */}
                  {viewMode === 'slider' && currentItem.processed_url && (
                    <>
                      {/* Before Image (Full) */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <img
                          src={currentItem.original_url}
                          alt="Before"
                          className="max-w-full max-h-full object-contain"
                          style={{ transform: `scale(${zoom})` }}
                          draggable={false}
                          onLoad={handleOriginalImageLoad}
                        />
                      </div>

                      {/* After Image (Clipped) */}
                      <div
                        className="absolute inset-0 flex items-center justify-center"
                        style={{ clipPath: `inset(0 0 0 ${sliderPosition}%)` }}
                      >
                        <img
                          src={currentItem.processed_url}
                          alt="After"
                          className="max-w-full max-h-full object-contain"
                          style={{ transform: `scale(${zoom})` }}
                          draggable={false}
                          onLoad={handleProcessedImageLoad}
                        />
                      </div>

                      {/* Slider Handle */}
                      <div
                        className="absolute top-0 bottom-0 w-0.5 bg-white cursor-ew-resize z-10"
                        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
                        onMouseDown={handleMouseDown}
                      >
                        {/* Handle Grip */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center cursor-ew-resize">
                          <GripVertical className="w-4 h-4 text-gray-600" />
                        </div>

                        {/* Labels */}
                        <div className="absolute top-4 right-3 bg-black/70 text-white text-xs font-medium px-2 py-1 rounded-full">
                          Before
                        </div>
                        <div className="absolute top-4 left-3 bg-white/90 text-gray-900 text-xs font-medium px-2 py-1 rounded-full">
                          After
                        </div>
                      </div>
                    </>
                  )}

                  {/* Split View */}
                  {viewMode === 'split' && (
                    <div className="absolute inset-0 flex gap-1 p-4">
                      {/* Original Panel */}
                      <div className="flex-1 flex flex-col">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2.5 h-2.5 rounded-full bg-gray-500" />
                          <span className="text-white/70 text-xs font-medium">Original</span>
                        </div>
                        <div className="flex-1 relative rounded-lg overflow-hidden bg-black/50 border border-white/10 flex items-center justify-center">
                          <img
                            src={currentItem.original_url}
                            alt="Original"
                            className="max-w-full max-h-full object-contain"
                            style={{ transform: `scale(${zoom})` }}
                            draggable={false}
                            onLoad={handleOriginalImageLoad}
                          />
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="flex items-center px-2">
                        <div className="w-px h-full bg-white/20" />
                      </div>

                      {/* Processed Panel */}
                      <div className="flex-1 flex flex-col">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                          <span className="text-emerald-400 text-xs font-medium">Processed</span>
                        </div>
                        <div className="flex-1 relative rounded-lg overflow-hidden bg-black/50 border border-emerald-500/30 flex items-center justify-center">
                          {currentItem.processed_url ? (
                            <img
                              src={currentItem.processed_url}
                              alt="Processed"
                              className="max-w-full max-h-full object-contain"
                              style={{ transform: `scale(${zoom})` }}
                              draggable={false}
                              onLoad={handleProcessedImageLoad}
                            />
                          ) : (
                            <div className="text-white/40 text-sm">No processed version</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Single View */}
                  {viewMode === 'single' && (
                    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                      <img
                        src={singleViewImage === 'processed' && currentItem.processed_url
                          ? currentItem.processed_url
                          : currentItem.original_url}
                        alt={singleViewImage === 'processed' ? 'Processed' : 'Original'}
                        className="object-contain rounded-lg"
                        style={{
                          transform: `scale(${zoom})`,
                          maxWidth: 'calc(100% - 2rem)',
                          maxHeight: 'calc(100% - 2rem)',
                        }}
                        draggable={false}
                        onLoad={singleViewImage === 'processed' ? handleProcessedImageLoad : handleOriginalImageLoad}
                      />
                      {/* Image Label */}
                      <div className={`absolute top-4 left-4 px-3 py-1.5 rounded-full text-xs font-medium ${
                        singleViewImage === 'processed'
                          ? 'bg-emerald-500/80 text-white'
                          : 'bg-black/60 text-white/80'
                      }`}>
                        {singleViewImage === 'processed' ? 'Processed' : 'Original'}
                      </div>
                    </div>
                  )}

                  {/* Slider fallback when no processed image */}
                  {viewMode === 'slider' && !currentItem.processed_url && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <img
                        src={currentItem.original_url}
                        alt="Original"
                        className="max-w-full max-h-full object-contain"
                        style={{ transform: `scale(${zoom})` }}
                        draggable={false}
                        onLoad={handleOriginalImageLoad}
                      />
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white/80 text-sm px-4 py-2 rounded-full">
                        No processed version available
                      </div>
                    </div>
                  )}

                  {/* Status Badge */}
                  <div className="absolute bottom-4 right-6 z-20">
                    <Badge className={`${STATUS_CONFIG[currentItem.review_status].bgColor} ${STATUS_CONFIG[currentItem.review_status].color} border ${STATUS_CONFIG[currentItem.review_status].borderColor}`}>
                      {STATUS_CONFIG[currentItem.review_status].icon}
                      <span className="ml-1.5">{STATUS_CONFIG[currentItem.review_status].label}</span>
                    </Badge>
                  </div>
                </div>

                {/* Revision Notes Bar */}
                {currentItem.review_notes && (
                  <div className="flex-shrink-0 px-6 py-3 bg-orange-500/10 border-t border-orange-500/30 w-full">
                    <div className="flex items-start gap-3">
                      <MessageSquare className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <span className="text-orange-400 text-xs font-medium uppercase tracking-wider">
                          Revision Notes
                        </span>
                        <p className="text-white/80 text-sm mt-1 whitespace-pre-wrap">
                          {currentItem.review_notes}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Bar - Compact */}
                <div className="flex-shrink-0 px-3 py-2 bg-black/50 border-t border-white/10 w-full">
                  <div className="flex items-center justify-between gap-2">
                    {/* Meta Info - Simplified */}
                    <div className="flex items-center gap-2 text-white/40 text-[10px] min-w-0 overflow-hidden">
                      {originalMeta && (
                        <span className="shrink-0 hidden sm:inline">{originalMeta.width}×{originalMeta.height}</span>
                      )}
                      {processedMeta && originalMeta &&
                       (processedMeta.width !== originalMeta.width || processedMeta.height !== originalMeta.height) && (
                        <span className="shrink-0 text-emerald-400 hidden md:inline">→ {processedMeta.width}×{processedMeta.height}</span>
                      )}
                      <span className="shrink-0 hidden lg:inline">
                        {format(new Date(currentItem.created_at), 'MMM d, h:mm a')}
                      </span>
                      {currentItem.revision_count > 0 && (
                        <span className="shrink-0 text-orange-400">Rev {currentItem.revision_count}</span>
                      )}
                    </div>

                    {/* Action Buttons - Compact */}
                    {canReview ? (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 px-2 border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                              onClick={() => {
                                setPendingAction('revision');
                                setShowNotesDialog(true);
                              }}
                              disabled={isSubmittingReview}
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              <span className="ml-1.5 hidden sm:inline">Revise</span>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Request Revision (V)</TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="destructive"
                              size="sm"
                              className="h-8 px-2"
                              onClick={() => handleQuickAction('reject')}
                              disabled={isSubmittingReview}
                            >
                              {isSubmittingReview ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <>
                                  <XCircle className="w-3.5 h-3.5" />
                                  <span className="ml-1.5 hidden sm:inline">Reject</span>
                                </>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Reject (R)</TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              className="h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => handleQuickAction('approve')}
                              disabled={isSubmittingReview}
                            >
                              {isSubmittingReview ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  <span className="ml-1.5">Approve</span>
                                </>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Approve (A)</TooltipContent>
                        </Tooltip>
                      </div>
                    ) : (
                      <div className="text-white/40 text-xs">
                        Already reviewed
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-white/40">
                <div className="text-center">
                  <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">No item selected</p>
                  <p className="text-sm mt-1">Select an item from the queue to review</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notes Dialog */}
        <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {pendingAction === 'revision' && 'Request Revision'}
                {pendingAction === 'approve' && 'Approve with Notes'}
                {pendingAction === 'reject' && 'Reject with Notes'}
              </DialogTitle>
              <DialogDescription>
                {pendingAction === 'revision'
                  ? 'Provide feedback on what needs to be changed.'
                  : 'Add optional notes for this review action.'}
              </DialogDescription>
            </DialogHeader>

            <Textarea
              placeholder={pendingAction === 'revision' ? 'Describe the required changes...' : 'Add notes (optional)...'}
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              rows={4}
              autoFocus
            />

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowNotesDialog(false);
                setReviewNotes('');
                setPendingAction(null);
              }}>
                Cancel
              </Button>
              <Button
                onClick={handleReviewWithNotes}
                disabled={isSubmittingReview || (pendingAction === 'revision' && !reviewNotes.trim())}
                className={pendingAction === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                variant={pendingAction === 'reject' ? 'destructive' : pendingAction === 'revision' ? 'default' : 'default'}
              >
                {isSubmittingReview ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* History Dialog */}
        <Dialog open={showHistory} onOpenChange={setShowHistory}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Review History
              </DialogTitle>
              <DialogDescription>
                {currentItem?.original_filename}
              </DialogDescription>
            </DialogHeader>

            {isLoadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No review history yet
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {history.map((entry) => {
                  const config = STATUS_CONFIG[entry.action as ReviewStatus] || STATUS_CONFIG.pending;
                  return (
                    <div key={entry.id} className={`flex gap-3 p-3 rounded-lg ${config.bgColor}`}>
                      <div className={config.color}>{config.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-sm capitalize">
                            {entry.action.replace(/_/g, ' ')}
                          </span>
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {format(new Date(entry.created_at), 'MMM d, h:mm a')}
                          </span>
                        </div>
                        {entry.reviewer && (
                          <p className="text-xs text-muted-foreground">{entry.reviewer.email}</p>
                        )}
                        {entry.notes && (
                          <div className="mt-2 p-2 bg-background/50 rounded text-sm">
                            <MessageSquare className="w-3 h-3 inline mr-1" />
                            {entry.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Keyboard Shortcuts Dialog */}
        <Dialog open={showKeyboardHelp} onOpenChange={setShowKeyboardHelp}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Keyboard className="w-5 h-5" />
                Keyboard Shortcuts
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-2">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-2">Navigation</p>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm">Next Item</span>
                <div className="flex gap-1">
                  <kbd className="px-2 py-0.5 text-xs bg-muted rounded">↓</kbd>
                  <kbd className="px-2 py-0.5 text-xs bg-muted rounded">J</kbd>
                </div>
              </div>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm">Previous Item</span>
                <div className="flex gap-1">
                  <kbd className="px-2 py-0.5 text-xs bg-muted rounded">↑</kbd>
                  <kbd className="px-2 py-0.5 text-xs bg-muted rounded">K</kbd>
                </div>
              </div>

              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mt-4 mb-2">Review Actions</p>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm text-emerald-500">Approve</span>
                <kbd className="px-2 py-0.5 text-xs bg-muted rounded">A</kbd>
              </div>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm text-red-500">Reject</span>
                <kbd className="px-2 py-0.5 text-xs bg-muted rounded">R</kbd>
              </div>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm text-orange-500">Request Revision</span>
                <kbd className="px-2 py-0.5 text-xs bg-muted rounded">V</kbd>
              </div>

              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mt-4 mb-2">View Modes</p>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm">Slider View</span>
                <kbd className="px-2 py-0.5 text-xs bg-muted rounded">1</kbd>
              </div>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm">Split View</span>
                <kbd className="px-2 py-0.5 text-xs bg-muted rounded">2</kbd>
              </div>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm">Single View</span>
                <kbd className="px-2 py-0.5 text-xs bg-muted rounded">3</kbd>
              </div>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm">Toggle Fullscreen</span>
                <kbd className="px-2 py-0.5 text-xs bg-muted rounded">F</kbd>
              </div>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm text-muted-foreground">Original (single view)</span>
                <kbd className="px-2 py-0.5 text-xs bg-muted rounded">O</kbd>
              </div>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm text-muted-foreground">Processed (single view)</span>
                <kbd className="px-2 py-0.5 text-xs bg-muted rounded">P</kbd>
              </div>

              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mt-4 mb-2">General</p>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm">Close / Exit Fullscreen</span>
                <kbd className="px-2 py-0.5 text-xs bg-muted rounded">Esc</kbd>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Download Modal */}
        <DownloadModal
          open={downloadModalOpen}
          onClose={() => setDownloadModalOpen(false)}
          downloadStats={downloadStats}
          totalProcessed={downloadStats?.totalProcessed || 0}
          onDownloadApproved={handleDownloadApproved}
          onDownloadAllApproved={handleDownloadAllApproved}
          onDownloadAll={handleDownloadAll}
        />
      </div>
    </TooltipProvider>
  );
}
